import { useState } from "react";
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Send } from "lucide-react";
import { FadeIn } from "@/components/common/FadeIn";
import { ThreadsIcon } from "@/components/common/ThreadsIcon";
import { TikTokIcon } from "@/components/common/TikTokIcon";
import { SEO } from "@/components/common/SEO";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", subscribe_newsletter: false });
  const [status, setStatus] = useState(null); // "success" | "error" | null
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      setStatus("error");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      setStatus("success");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", subscribe_newsletter: false });
    } catch {
      setStatus("error");
    }
    setLoading(false);
  };

  const update = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const toggle = (field) => (e) => setForm({ ...form, [field]: e.target.checked });

  return (
    <div data-testid="contact-page" className="pt-20">
      <SEO
        title="Contact | Rain Boy Fine Art Gallery Tulsa, OK"
        description="Visit Rain Boy Fine Art at 3306 Charles Page Blvd #218, Tulsa, OK 74127. Open Monday–Saturday 11 AM – 6 PM. Sunday by appointment. Call (405) 885-9010 or email us."
        path="/contact"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]}
        schemas={[{ "@context": "https://schema.org", "@type": "ContactPage", name: "Contact Rain Boy Fine Art", url: "https://rainboyfineart.com/contact", mainEntity: { "@type": "ArtGallery", "@id": "https://rainboyfineart.com/#gallery" } }]}
      />
      <section className="py-16 md:py-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <p className="overline mb-4">Get In Touch</p>
            <h1 className="section-heading font-heading text-4xl sm:text-5xl lg:text-6xl text-[#0A1128] mb-8">
              Contact Us
            </h1>
          </FadeIn>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-12 lg:gap-16">
            {/* Form */}
            <FadeIn delay={0.1}>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="text-xs uppercase tracking-[0.15em] text-[#8A94A6] mb-2 block">Name *</Label>
                    <Input id="name" value={form.name} onChange={update("name")} className="rounded-none border-[#0A1128]/15 focus:border-[#0A1128] focus:ring-[#0A1128]" data-testid="contact-name" required />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-xs uppercase tracking-[0.15em] text-[#8A94A6] mb-2 block">Email *</Label>
                    <Input id="email" type="email" value={form.email} onChange={update("email")} className="rounded-none border-[#0A1128]/15 focus:border-[#0A1128] focus:ring-[#0A1128]" data-testid="contact-email" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="text-xs uppercase tracking-[0.15em] text-[#8A94A6] mb-2 block">Phone</Label>
                    <Input id="phone" value={form.phone} onChange={update("phone")} className="rounded-none border-[#0A1128]/15 focus:border-[#0A1128] focus:ring-[#0A1128]" data-testid="contact-phone" />
                  </div>
                  <div>
                    <Label htmlFor="subject" className="text-xs uppercase tracking-[0.15em] text-[#8A94A6] mb-2 block">Subject *</Label>
                    <Input id="subject" value={form.subject} onChange={update("subject")} className="rounded-none border-[#0A1128]/15 focus:border-[#0A1128] focus:ring-[#0A1128]" data-testid="contact-subject" required />
                  </div>
                </div>
                <div>
                  <Label htmlFor="message" className="text-xs uppercase tracking-[0.15em] text-[#8A94A6] mb-2 block">Message *</Label>
                  <Textarea id="message" rows={6} value={form.message} onChange={update("message")} className="rounded-none border-[#0A1128]/15 focus:border-[#0A1128] focus:ring-[#0A1128] resize-none" data-testid="contact-message" required />
                </div>

                {/* Newsletter opt-in. Adds the visitor to the Klaviyo Newsletter
                    list (Rj8phF) when checked. Styled as a custom checkbox so
                    the gold accent reads as part of the brand system. */}
                <label
                  htmlFor="contact-subscribe-newsletter"
                  className="flex items-start gap-3 cursor-pointer select-none"
                  data-testid="contact-newsletter-label"
                >
                  <input
                    id="contact-subscribe-newsletter"
                    type="checkbox"
                    checked={form.subscribe_newsletter}
                    onChange={toggle("subscribe_newsletter")}
                    className="mt-1 h-4 w-4 rounded-none border border-[#0A1128]/30 text-[#CBA365] focus:ring-1 focus:ring-[#CBA365] accent-[#CBA365] cursor-pointer"
                    data-testid="contact-subscribe-newsletter"
                  />
                  <span
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: "0.85rem",
                      color: "rgba(10, 25, 41, 0.75)",
                      lineHeight: 1.5,
                    }}
                  >
                    Subscribe me to the Rain Boy Fine Art newsletter for updates on new work, upcoming shows, and exclusive events.
                  </span>
                </label>

                {status === "success" && (
                  <p className="text-green-600 text-sm" data-testid="contact-success">Thank you! Your message has been sent.</p>
                )}
                {status === "error" && (
                  <p className="text-red-500 text-sm" data-testid="contact-error">Please fill in all required fields.</p>
                )}

                <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
                  <button
                    type="submit"
                    disabled={loading}
                    className="inquire-sweep relative overflow-hidden inline-flex items-center justify-center gap-3 bg-[#0A1128] text-white px-8 py-4 text-xs uppercase tracking-[0.2em] disabled:opacity-50 self-start lg:flex-shrink-0"
                    style={{ transition: "color 0.45s ease" }}
                    data-testid="contact-submit"
                  >
                    <span aria-hidden="true" className="inquire-sweep__fill absolute inset-0" style={{ backgroundColor: "#CBA365" }} />
                    <span className="relative z-10 inline-flex items-center gap-3">
                      {loading ? "Sending..." : "Send Message"} <Send size={14} />
                    </span>
                  </button>
                  <p className="text-[11px] text-[#8A94A6] leading-relaxed max-w-md lg:mt-0">
                    By submitting this form, you agree that Rain Boy Fine Art may contact you with news, upcoming exhibitions, and special offers. You can unsubscribe at any time.
                  </p>
                </div>
              </form>
            </FadeIn>

            {/* Info */}
            <FadeIn delay={0.2}>
              <div className="space-y-8">
                <div>
                  <h3 className="font-heading text-lg text-[#0A1128] mb-4">Gallery Information</h3>
                  <div className="space-y-3 text-sm text-[#4A5568]">
                    <div className="flex items-start gap-3">
                      <MapPin size={14} className="mt-1 shrink-0 text-[#CBA365]" />
                      <span>3306 Charles Page Blvd #218<br />Tulsa, OK 74127</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone size={14} className="shrink-0 text-[#CBA365]" />
                      <span>(405) 885-9010</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Mail size={14} className="shrink-0 text-[#CBA365]" />
                      <span>info@rainboyfineart.com</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-heading text-lg text-[#0A1128] mb-4">Hours</h3>
                  <div className="relative inline-block px-6 py-5">
                    <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-[#CBA365]" />
                    <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-[#CBA365]" />
                    <div className="space-y-1 text-sm text-[#4A5568]">
                      <p>Monday - Saturday: 11 AM - 6 PM</p>
                      <p>Sunday: By Appointment</p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <a href="https://www.facebook.com/profile.php?id=61581586250780" target="_blank" rel="noopener noreferrer" className="text-[#8A94A6] hover:text-[#0A1128] transition-colors" data-testid="contact-facebook">
                    <Facebook size={20} />
                  </a>
                  <a href="https://www.instagram.com/rainboyfineart" target="_blank" rel="noopener noreferrer" className="text-[#8A94A6] hover:text-[#0A1128] transition-colors" data-testid="contact-instagram">
                    <Instagram size={20} />
                  </a>
                  <a href="https://www.threads.com/@rainboyfineart" target="_blank" rel="noopener noreferrer" className="text-[#8A94A6] hover:text-[#0A1128] transition-colors" data-testid="contact-threads">
                    <ThreadsIcon size={18} />
                  </a>
                  <a href="https://www.tiktok.com/@rainboyfineart" target="_blank" rel="noopener noreferrer" className="text-[#8A94A6] hover:text-[#0A1128] transition-colors" data-testid="contact-tiktok">
                    <TikTokIcon size={18} />
                  </a>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="px-6 lg:px-12 pb-16" data-testid="map-section">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="aspect-[16/6] min-h-[300px] bg-[#F8F9FA]">
              <iframe
                title="Gallery Location"
                src="https://maps.google.com/maps?q=3306+Charles+Page+Blvd+%23266+Tulsa+OK+74127&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0, minHeight: 300 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                data-testid="google-map"
              />
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
